import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  effect,
  input,
  output,
  signal,
  untracked,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { ButtonComponent } from '@examdojo/core/button';
import { NumberInputComponent } from '@examdojo/core/input';
import { JsonEditorComponent } from '@examdojo/core/json-editor';
import { IonContent, IonHeader, IonModal, IonTitle, IonToolbar } from '@ionic/angular/standalone';
import type { EChartsOption } from 'echarts';
import { debounceTime, tap } from 'rxjs';
import { EchartsSvgComponent } from '../../../echarts/echarts-svg.component';
import { EchartsJsonEditorDirective } from './echarts-json-editor.directive';
import { ECHARTS_BOILERPLATE_JSON } from './echarts.model';

const ECHARTS_DOCS_URL = 'https://echarts.apache.org/en/option.html';

@Component({
  selector: 'dojo-echarts-editor',
  standalone: true,
  imports: [
    IonModal,
    IonHeader,
    IonToolbar,
    IonTitle,
    IonContent,
    ButtonComponent,
    ReactiveFormsModule,
    NumberInputComponent,
    EchartsSvgComponent,
    JsonEditorComponent,
    EchartsJsonEditorDirective,
  ],
  templateUrl: './echarts-editor.component.html',
  styleUrl: './echarts-editor.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EchartsEditorComponent {
  constructor(private readonly destroyRef: DestroyRef) {
    // Sync input options into form control when modal opens
    effect(() => {
      if (this.isOpen()) {
        const opts = untracked(() => this.options());
        const height = untracked(() => this.height());
        const isEmpty = !opts || Object.keys(opts).length === 0;
        const json = isEmpty ? ECHARTS_BOILERPLATE_JSON : JSON.stringify(opts, null, 2);
        this.jsonCtrl.setValue(json, { emitEvent: false });
        this.heightCtrl.setValue(height);
        this.updatePreview(json);
      }
    });

    // Debounced: Monaco form control → preview
    this.jsonCtrl.valueChanges
      .pipe(
        debounceTime(400),
        tap((value) => this.updatePreview(value)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  readonly options = input.required<EChartsOption>();
  readonly height = input<number>(400);
  readonly isOpen = input<boolean>(false);

  readonly optionsChange = output<{ options: EChartsOption; height?: number }>();
  readonly editorClose = output<void>();

  readonly jsonCtrl = new FormControl<string>('{}', { nonNullable: true });
  readonly heightCtrl = new FormControl<number>(400, { nonNullable: true });
  readonly previewOptions = signal<EChartsOption | null>(null);
  readonly docsUrl = ECHARTS_DOCS_URL;

  save(): void {
    if (this.jsonCtrl.invalid) {
      return;
    }

    try {
      const parsed = JSON.parse(this.jsonCtrl.value) as EChartsOption;
      this.optionsChange.emit({ options: parsed, height: this.heightCtrl.value });
    } catch {
      // Monaco validation should prevent this, but guard anyway
      return;
    }
    this.editorClose.emit();
  }

  cancel(): void {
    this.editorClose.emit();
  }

  onDidDismiss(): void {
    this.editorClose.emit();
  }

  private updatePreview(value: string): void {
    try {
      const parsed = JSON.parse(value) as EChartsOption;
      this.previewOptions.set(parsed);
    } catch {
      this.previewOptions.set(null);
    }
  }
}
